import { Badge, Box, CircularProgress, Fab, IconButton, Paper, Typography } from '@mui/material';
import { Close, Domain, Save } from '@mui/icons-material';
import { ActivateSaveButtonFrom, PanelTitle } from '../utils';
import { ITenant } from 'types/tenant';
import { useTheme } from '@mui/styles';
import { useState } from 'react';
import { EntitiesAssociatedDropdown } from 'ui-component/records/dropdowns/EntitiesAssociatedDropdown';
import { useMutation } from '@apollo/client';
import { MUTATION_UPDATE_RECORD_HEADER } from 'graphql/mutations/bills';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/slices/snackbar';
import { StyledTooltip } from 'ui-component/RecordView/components/StyledTooltip';
import { ConditionalWrapper } from 'ui-component/ConditionalWrapper';

export type FooterProps = {
    panelState: PanelTitle;
    onSave?: () => Promise<void>;
    onCancel?: () => void;
    isCreate?: boolean;
    disabledButtons?: boolean;
    businessEntity?: Pick<ITenant, 'id' | 'name'> | null | undefined;
    recordTypeId?: number;
    recordDataId?: number;
    from?: 'data_focus_view' | 'portal' | 'document_viewer';
    objectEditingRestricted?: boolean;
    allowedToAccessEntities?: boolean;
};

const FooterOptions = ({
    panelState,
    onSave,
    onCancel,
    isCreate,
    disabledButtons,
    businessEntity,
    recordTypeId,
    recordDataId,
    from,
    objectEditingRestricted,
    allowedToAccessEntities
}: FooterProps) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState(businessEntity?.id);

    const theme = useTheme();
    const storeDispatch = useDispatch();

    const [updateRecordHeader] = useMutation<{ updateRecordHeader: any }>(MUTATION_UPDATE_RECORD_HEADER);

    const handleOnSave = async () => {
        await onSave?.();
    };

    const handleOnCancel = () => {
        onCancel?.();
    };

    const handleOnEntity = () => {
        // handleToggleEntities?.();
        setShowDropdown((value) => !value);
    };

    const handleOnSaveEntity = async (entity: string) => {
        try {
            await updateRecordHeader({
                variables: {
                    data: {
                        id: Number(recordDataId),
                        businessEntity: Number(entity)
                    }
                }
            });

            storeDispatch(
                openSnackbar({
                    open: true,
                    message: 'Data saved!',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (err: any) {
            storeDispatch(
                openSnackbar({
                    open: true,
                    message: `Save changes failed: ${err.message || 'Internal server error.'}`,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: true
                })
            );
        }
        setShowDropdown(false);
    };

    const isEditPanel = panelState === PanelTitle.Edit || panelState === PanelTitle.EditObject;

    const isFromDataFocusViewOrPortal = from === ActivateSaveButtonFrom.DataFocusView || from === ActivateSaveButtonFrom.Portal;

    const checkIfRestricted = () => {
        if (isEditPanel && !isFromDataFocusViewOrPortal) {
            if (panelState === PanelTitle.EditObject && !objectEditingRestricted) {
                return true;
            }
            if (panelState === PanelTitle.Edit) {
                return true;
            }
        }

        return false;
    };

    const STORAGE_KEY = 'rightHandPanelWidth';
    const panelWidth = localStorage.getItem(STORAGE_KEY);
    const showSaveButton = isEditPanel && isFromDataFocusViewOrPortal;

    return (
        <Box>
            {!isCreate && (
                <Box sx={{ position: 'relative' }} gap={1} mb={1}>
                    {showDropdown && (
                        <Box
                            sx={{
                                position: 'fixed',
                                zIndex: 1,
                                right: 75,
                                bottom: showSaveButton ? 45 : 10,
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: 1
                            }}
                        >
                            <Paper elevation={1}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        p: 0.5,
                                        width: `calc(${panelWidth}px - 4rem)`,
                                        height: '3.25rem',
                                        bgcolor: theme.palette.primary[300]
                                    }}
                                >
                                    <Typography variant="caption" sx={{ p: 1 }}>
                                        Entity:
                                    </Typography>

                                    <EntitiesAssociatedDropdown
                                        recordTypeId={Number(recordTypeId)}
                                        onChange={(value) => {
                                            setSelectedEntity(value as string);
                                            handleOnSaveEntity(value as string);
                                        }}
                                        initialValue={selectedEntity || businessEntity?.id}
                                        PopoverSx={{
                                            top: '-15px',
                                            left: '-70px'
                                        }}
                                        PopoverPropsAnchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left'
                                        }}
                                        PopoverPropsTransformOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center'
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    )}
                    {allowedToAccessEntities && (
                        <StyledTooltip title="Entities">
                            <ConditionalWrapper
                                condition={!businessEntity}
                                wrapper={(children) => (
                                    <Badge variant="dot" color="error">
                                        {children}
                                    </Badge>
                                )}
                            >
                                <IconButton
                                    onClick={handleOnEntity}
                                    size="medium"
                                    sx={{
                                        color: theme.palette.primary[400],
                                        bgcolor: showDropdown
                                            ? theme.palette.secondary.light
                                            : `${theme.palette.background.paper} !important`,
                                        fontWeight: 600,
                                        height: '100%',
                                        '&:hover': { color: theme.palette.primary[400], backgroundColor: theme.palette.primary[300] }
                                    }}
                                >
                                    <Domain />
                                </IconButton>
                            </ConditionalWrapper>
                        </StyledTooltip>
                    )}
                </Box>
            )}

            {checkIfRestricted() && (
                <Box gap={1} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    {!isCreate && (
                        <StyledTooltip title="Discard Changes">
                            <Fab
                                onClick={handleOnCancel}
                                sx={{ width: 40, height: 40, mb: 1, boxShadow: 'none', '& svg': { color: '#858585' } }}
                            >
                                <Close />
                            </Fab>
                        </StyledTooltip>
                    )}
                    <StyledTooltip title="Save Changes">
                        <Fab
                            data-testid="create-record-save-button"
                            color="secondary"
                            onClick={handleOnSave}
                            sx={{ width: 40, height: 40, boxShadow: 'none' }}
                            disabled={disabledButtons}
                        >
                            {disabledButtons ? <CircularProgress size={20} color="inherit" /> : <Save />}
                        </Fab>
                    </StyledTooltip>
                </Box>
            )}

            {showSaveButton && (
                <StyledTooltip title="Save">
                    <Fab
                        data-testid="create-record-save-button"
                        color="secondary"
                        onClick={onSave}
                        sx={{ width: 40, height: 40 }}
                        disabled={disabledButtons}
                    >
                        <Save />
                    </Fab>
                </StyledTooltip>
            )}
        </Box>
    );
};
export default FooterOptions;
